/**
 * The one photo card (filter-table-reuse 04, accepted prototype direction C).
 *
 * Every photo surface uses this shell: gallery, search photo hits, item photos
 * and container file photos. Rules:
 *
 * - media is **fit** (`contain`), never cropped;
 * - the caption is below the image. The default `title` variant is two lines:
 *   title, then metadata (gallery: container name + path). The `file` variant
 *   drops the title and keeps the file name as the caption line, for surfaces
 *   whose own header already names the container (container view);
 * - the item type appears on the second line and only for `nutrition`
 *   (`general` is never rendered);
 * - the only control on the image is the info action: a light scrim (~16% black)
 *   with a softened glyph, recognisable on a second glance, not prominent. By
 *   default it opens the shared detail panel; a read-only consumer (gallery
 *   lightbox) can pass `onInfo` to use it as a direct action instead;
 * - an optional caption click keeps the search photo's second target (caption →
 *   container) without adding another overlay.
 *
 * Accessible names never read a hidden title. In the `file` variant the media and
 * info actions fall back to the file name, then the caller's media label, then
 * the image alt, so screen readers always get a non-empty name.
 *
 * The detail panel is the P2 preview: right drawer on desktop, bottom sheet on
 * phones, with the sibling strip (max 32, passed by the caller), actions and the
 * container identity. The card's photo is the initial selection; a sibling
 * thumbnail selects that photo in the panel (it never opens the lightbox), and
 * the large image is the panel's only lightbox trigger. The panel names the
 * selected photo: `panelTitle`/`panelSecondary` name the card's own photo
 * (defaulting to the caption's `title`/`secondary`), while each related entry
 * carries its own `title`/`secondary`. It is an overlay, so
 * the virtualized gallery rows keep their measured height (ADR-0031).
 */

import { useState, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { DetailPanel } from "./DetailPanel";
import { IdentityLine } from "./IdentityLine";
import { InfoOutlinedIcon } from "./icons";
import { useT } from "../i18n";

/**
 * One photo in the panel's selectable set. Every entry carries the data the
 * panel renders for its photo, so selecting a sibling never falls back to the
 * card's original title, identity or actions.
 */
export interface PhotoCardRelated {
  key: string;
  image: string;
  alt: string;
  /** Panel header for this photo (the card's own `panelTitle` for the initial entry). */
  title: string;
  /** Panel subtitle (path, owning container, photo kind); empty renders nothing. */
  secondary?: string;
  /** `Nutrition` only; never pass `General`. */
  typeLabel?: string;
  /** Container identity shown while this photo is selected. */
  identity?: { uuid: string; name: string };
  /** Actions shown while this photo is selected. */
  actions: readonly PhotoCardAction[];
  /** Open the lightbox at this photo. */
  onOpen: () => void;
}

/**
 * `title` (default): bold title line plus the secondary line (gallery: container
 * name + path). `file`: no title, the secondary (file name) stays a plain caption
 * line — for the container view, whose shell header already names the viewed
 * container. Type labels belong to the `title` variant.
 */
export type PhotoCardCaptionVariant = "title" | "file";

/** The shared card caption, with the title-hidden `file` variant. */
export function PhotoCardCaption({
  variant = "title",
  title,
  secondary,
  typeLabel,
  titleTestId,
  secondaryTestId,
}: {
  variant?: PhotoCardCaptionVariant;
  title: string;
  secondary?: string;
  typeLabel?: string;
  titleTestId?: string;
  secondaryTestId?: string;
}) {
  const hasSecondary = secondary !== undefined && secondary !== "";
  return (
    <Box sx={{ minWidth: 0, flexGrow: 1, textAlign: "left" }}>
      {variant === "title" && (
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600 }}
          data-testid={titleTestId}
        >
          {title}
        </Typography>
      )}
      {variant === "file" ? (
        hasSecondary && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            data-testid={secondaryTestId}
          >
            {secondary}
          </Typography>
        )
      ) : (
        <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ minWidth: 0 }}>
          {typeLabel !== undefined && (
            <Typography
              variant="caption"
              sx={{ color: "primary.main", fontWeight: 700, flexShrink: 0 }}
            >
              {typeLabel}
            </Typography>
          )}
          {hasSecondary && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ minWidth: 0 }}
              data-testid={secondaryTestId}
            >
              {secondary}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

export interface PhotoCardAction {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Optional accessible name when the visible label is not specific enough. */
  ariaLabel?: string;
}

export interface PhotoCardProps {
  image: string;
  imageAlt: string;
  title: string;
  /** Caption variant; `file` hides the title and keeps the file name as caption. */
  captionVariant?: PhotoCardCaptionVariant;
  /** Second caption line (path, file name); empty renders nothing. */
  secondary?: string;
  /** Panel header for the card's own photo; defaults to `title`. */
  panelTitle?: string;
  /** Panel subtitle for the card's own photo; defaults to `secondary`. */
  panelSecondary?: string;
  /** `Nutrition` only; never pass `General`. */
  typeLabel?: string;
  /** Photo count badge; rendered only above 1. */
  count?: number;
  /** Media click (lightbox / zoom). */
  onOpen?: () => void;
  /** Caption click — the search photo's "go to container" target. */
  onCaptionClick?: () => void;
  /** Use the info action as a direct action instead of opening the sheet. */
  onInfo?: () => void;
  /** Related photos shown in the detail panel (caller caps the list). */
  related?: readonly PhotoCardRelated[];
  /** Actions shown in the detail panel. */
  actions?: readonly PhotoCardAction[];
  /** Container identity shown in the sheet — never an item/photo UUID. */
  identity?: { uuid: string; name: string };
  /** Extra sheet content (transcript, metadata, …). */
  children?: ReactNode;
  cardTestId?: string;
  mediaTestId?: string;
  captionTestId?: string;
  /** Accessible media label; defaults to "Open <title>". */
  mediaLabel?: string;
  titleTestId?: string;
  secondaryTestId?: string;
}

export function PhotoCard({
  image,
  imageAlt,
  title,
  captionVariant = "title",
  secondary,
  panelTitle,
  panelSecondary,
  typeLabel,
  count,
  onOpen,
  onCaptionClick,
  onInfo,
  related = [],
  actions = [],
  identity,
  children,
  cardTestId = "photo-card",
  mediaTestId,
  captionTestId,
  mediaLabel,
  titleTestId,
  secondaryTestId,
}: PhotoCardProps) {
  const t = useT();
  const [sheetOpen, setSheetOpen] = useState(false);
  const hasSheet = actions.length > 0 || children !== undefined;
  const showInfo = onInfo !== undefined || hasSheet;

  const media = (
    <Box
      component="img"
      src={image}
      alt={imageAlt}
      loading="lazy"
      sx={{
        width: "100%",
        aspectRatio: "1",
        objectFit: "contain",
        display: "block",
        bgcolor: "action.hover",
      }}
    />
  );

  // Screen-reader name for the media/info actions. The `file` variant never
  // names them after the hidden title: it falls back to the file name, then the
  // caller's media label, then the image alt, so the name is never empty.
  const accessibleName =
    captionVariant === "file" ? (secondary || mediaLabel || imageAlt) : title;

  const caption = (
    <PhotoCardCaption
      variant={captionVariant}
      title={title}
      secondary={secondary}
      typeLabel={typeLabel}
      titleTestId={titleTestId}
      secondaryTestId={secondaryTestId}
    />
  );

  return (
    <>
      <Box
        data-testid={cardTestId}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box sx={{ position: "relative" }}>
          {onOpen ? (
            <ButtonBase
              onClick={onOpen}
              aria-label={mediaLabel ?? `${t("searchOpen")} ${accessibleName}`}
              data-testid={mediaTestId}
              sx={{ display: "block", width: "100%", cursor: "zoom-in" }}
            >
              {media}
            </ButtonBase>
          ) : (
            <Box data-testid={mediaTestId}>{media}</Box>
          )}
          {count !== undefined && count > 1 && (
            <Box
              sx={{
                position: "absolute",
                top: 6,
                left: 6,
                px: 0.75,
                borderRadius: 1,
                bgcolor: "#23272a",
                color: "common.white",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {count}
              </Typography>
            </Box>
          )}
          {showInfo && (
            <IconButton
              size="small"
              data-testid="photo-info"
              aria-label={`${t("details")} ${accessibleName}`}
              onClick={() => {
                if (onInfo) onInfo();
                else setSheetOpen(true);
              }}
              sx={{
                position: "absolute",
                bottom: 6,
                right: 6,
                color: (theme) => alpha(theme.palette.common.white, 0.82),
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.16),
                "&:hover": {
                  color: "common.white",
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.32),
                },
                "&:focus-visible": {
                  color: "common.white",
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.32),
                },
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {onCaptionClick ? (
          <ButtonBase
            onClick={onCaptionClick}
            data-testid={captionTestId}
            sx={{ display: "block", width: "100%", p: { xs: 0.5, sm: 0.75 }, textAlign: "left" }}
          >
            {caption}
          </ButtonBase>
        ) : (
          <Box sx={{ p: { xs: 0.5, sm: 0.75 } }} data-testid={captionTestId}>
            {caption}
          </Box>
        )}
      </Box>

      {hasSheet && !onInfo && (
        <PhotoDetailPanel
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          image={image}
          imageAlt={imageAlt}
          title={title}
          secondary={secondary}
          panelTitle={panelTitle}
          panelSecondary={panelSecondary}
          typeLabel={typeLabel}
          identity={identity}
          onOpen={onOpen}
          related={related}
          actions={actions}
        >
          {children}
        </PhotoDetailPanel>
      )}
    </>
  );
}

/** The card's own photo plus its related entries, in panel order. */
interface PhotoPanelEntry {
  key: string;
  image: string;
  alt: string;
  title: string;
  secondary?: string;
  typeLabel?: string;
  identity?: { uuid: string; name: string };
  actions: readonly PhotoCardAction[];
  onOpen?: () => void;
}

/** The card's own photo is always the first selectable entry (its initial state). */
const OWN_PHOTO_KEY = "own";

function PhotoDetailPanel({
  open,
  onClose,
  image,
  imageAlt,
  title,
  secondary,
  panelTitle,
  panelSecondary,
  typeLabel,
  identity,
  onOpen,
  related,
  actions,
  children,
}: {
  open: boolean;
  onClose: () => void;
  image: string;
  imageAlt: string;
  title: string;
  secondary?: string;
  panelTitle?: string;
  panelSecondary?: string;
  typeLabel?: string;
  identity?: { uuid: string; name: string };
  onOpen?: () => void;
  related: readonly PhotoCardRelated[];
  actions: readonly PhotoCardAction[];
  children?: ReactNode;
}) {
  const t = useT();
  const [selectedKey, setSelectedKey] = useState(OWN_PHOTO_KEY);
  const own: PhotoPanelEntry = {
    key: OWN_PHOTO_KEY,
    image,
    alt: imageAlt,
    // The panel names the photo, not the card's caption target: callers pass
    // the photo name here (file name, item name), falling back to the caption.
    title: panelTitle ?? title,
    secondary: panelSecondary ?? secondary,
    typeLabel,
    identity,
    actions,
    onOpen,
  };
  // The card's photo is a first-class selection: the strip is derived from the
  // whole set, so the photo the user came from reappears once a sibling is
  // selected.
  const selectable = [own, ...related.filter((entry) => entry.key !== OWN_PHOTO_KEY)];
  const selected = selectable.find((entry) => entry.key === selectedKey) ?? own;
  const siblings = selectable.filter((entry) => entry.key !== selected.key);
  const close = () => {
    setSelectedKey(OWN_PHOTO_KEY);
    onClose();
  };
  const selectedName = selected.alt || selected.title;
  const preview = (
    <Box
      component="img"
      src={selected.image}
      alt={selected.alt}
      sx={{
        width: "100%",
        maxHeight: 300,
        objectFit: "contain",
        bgcolor: "action.hover",
        borderRadius: 1,
        display: "block",
      }}
    />
  );
  return (
    <DetailPanel
      open={open}
      onClose={close}
      title={selected.title}
      testId="photo-card-sheet"
      subtitle={
        <Stack direction="row" spacing={0.5} alignItems="baseline">
          {selected.typeLabel !== undefined && (
            <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700 }}>
              {selected.typeLabel}
            </Typography>
          )}
          {selected.secondary !== undefined && selected.secondary !== "" && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {selected.secondary}
            </Typography>
          )}
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        {selected.onOpen ? (
          <ButtonBase
            onClick={selected.onOpen}
            aria-label={`${t("openPhoto")} ${selectedName}`}
            data-testid="photo-panel-image"
            sx={{ display: "block", width: "100%", borderRadius: 1, cursor: "zoom-in" }}
          >
            {preview}
          </ButtonBase>
        ) : (
          <Box data-testid="photo-panel-image">{preview}</Box>
        )}
        {siblings.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {t("relatedPhotos")} · {siblings.length}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ overflowX: "auto", pb: 0.5, scrollbarWidth: "thin" }}
            >
              {siblings.map((sibling) => (
                <ButtonBase
                  key={sibling.key}
                  onClick={() => setSelectedKey(sibling.key)}
                  aria-label={sibling.alt || sibling.title}
                  data-testid="photo-related"
                  sx={{ width: 64, height: 64, flexShrink: 0, borderRadius: 1, overflow: "hidden" }}
                >
                  <Box
                    component="img"
                    src={sibling.image}
                    alt={sibling.alt}
                    loading="lazy"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      bgcolor: "action.hover",
                    }}
                  />
                </ButtonBase>
              ))}
            </Stack>
          </Box>
        )}
        {selected.identity !== undefined && selected.identity.uuid !== "" && (
          <IdentityLine uuid={selected.identity.uuid} name={selected.identity.name} />
        )}
        {selected.actions.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {selected.actions.map((action) => (
              <Button
                key={action.key}
                size="small"
                startIcon={action.icon}
                disabled={action.disabled}
                aria-label={action.ariaLabel}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
        )}
        {children}
      </Stack>
    </DetailPanel>
  );
}
